import React from 'react'
import { NavTooltip } from './NavTooltip'
import { cn } from 'components/UI/cn'

/**
 * A reusable icon button component for the left navigation bar.
 *
 * Features:
 * - Hover and selection states with consistent styling
 * - Dynamic icon selection based on state (filled vs outline)
 * - Optional text label display below icon (FIR-4955)
 * - Integration with navigation tooltip (when label is hidden)
 *
 * Layout Specs:
 * - Button: 44x44px with 8px border radius
 * - Label (when shown): Inter Medium 9px, 16px line-height, 0px gap from icon
 * - Spacing: 20px gap between nav items (controlled by parent)
 *
 * @example
 * // With visible label
 * <LeftNavIconButton
 *   icon={<PiHouseFill size={24} />}
 *   outlineIcon={<PiHouse size={24} />}
 *   label="Home"
 *   isSelected={true}
 *   showLabel={true}
 *   onClick={handleClick}
 * />
 */
interface LeftNavIconButtonProps {
  icon: React.ReactNode
  outlineIcon?: React.ReactNode
  label: string
  isSelected?: boolean
  isSecondaryActive?: boolean // Maintains hover/active state when this section's slide-out is open
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  showLabel?: boolean // When true, displays text label below icon; when false, shows tooltip on hover
  suppressTooltip?: boolean // When true, suppresses the tooltip (e.g., when a popover menu is open)
}

export const LeftNavIconButton = React.forwardRef<
  HTMLButtonElement,
  LeftNavIconButtonProps
>(
  (
    {
      icon,
      outlineIcon,
      label,
      isSelected = false,
      isSecondaryActive = false,
      onClick,
      className,
      showLabel = false,
      suppressTooltip = false,
    },
    ref
  ) => {
    const renderedIcon = isSelected ? icon : (outlineIcon ?? icon)

    // Hover is handled via CSS in main.css ([data-left-nav] button:hover)
    // This works around Bootstrap's button resets overriding Tailwind hover classes
    const buttonElement = (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={cn(
          'flex items-center justify-center w-[44px] h-[44px] rounded-lg border-none text-white cursor-pointer transition-colors duration-150',
          isSelected || isSecondaryActive
            ? 'bg-[var(--nav-bg-hover)]'
            : 'bg-transparent',
          className
        )}
        aria-label={label}
      >
        {renderedIcon}
      </button>
    )

    if (showLabel) {
      // When showing label, wrap button and label in a container with no gap (0px between icon and label per Figma)
      return (
        <div className="flex flex-col items-center w-full">
          {buttonElement}
          <span className="text-white text-[9px] font-medium h-4 flex items-center justify-center w-full text-center leading-[16px]">
            {label}
          </span>
        </div>
      )
    }

    if (suppressTooltip) return buttonElement

    return <NavTooltip label={label}>{buttonElement}</NavTooltip>
  }
)
LeftNavIconButton.displayName = 'LeftNavIconButton'
